import React, { useEffect, useState } from 'react';
import { Box, Chip, Container, IconButton, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import {
  CheckCircle as ConnectedIcon,
  Error as ErrorIcon,
  Hub as BackendIcon,
  RadioButtonUnchecked as IdleIcon,
  StopCircle as StopIcon,
  Sync as SyncIcon,
  TaskAlt as TaskIcon,
} from '@mui/icons-material';
import { useGlobal } from '../context/GlobalContext';
import { SUPPORTED_CHAINS } from '../config/chains';
import { formatIntervalSeconds } from '../utils/blockTiming';
import { mempoolWebSocketService } from '../services/mempoolWebSocketService';
import { backendRpcService } from '../services/backendRpcService';

const PERSISTENT_TASK_LABELS = {
  mempool: 'Mempool 監聽',
  pending: 'Pending 監聽',
  blockrazor_copy: 'Copy Trading 串流',
  blockrazor_public: 'BlockRazor Public Mempool',
  frontrun: 'Frontrun 預熱執行',
};

const getBackendColor = (state) => {
  if (state === 'connected') return 'success';
  if (state === 'connecting' || state === 'reconnecting') return 'warning';
  return 'default';
};

const getBackendLabel = (state) => ({
  connected: 'Backend 已連接',
  connecting: 'Backend 連接中',
  reconnecting: 'Backend 重連中',
  closing: 'Backend 關閉中',
  closed: 'Backend 已關閉',
  disconnected: 'Backend 未連接',
}[state] || `Backend ${state || '未知'}`);

const GlobalStatusBar = () => {
  const { selectedChain, selectedDex, rpcStatus } = useGlobal();
  const [backendStatus, setBackendStatus] = useState(() =>
    mempoolWebSocketService.getStatusSnapshot()
  );
  const [taskStatus, setTaskStatus] = useState(() =>
    mempoolWebSocketService.getPersistentTaskStatus?.() || { tasks: [] }
  );
  const [taskMenuAnchor, setTaskMenuAnchor] = useState(null);
  const [stoppingTaskId, setStoppingTaskId] = useState('');

  useEffect(() => {
    const handleState = (snapshot) => setBackendStatus(snapshot);
    mempoolWebSocketService.on('state', handleState);
    return () => mempoolWebSocketService.off('state', handleState);
  }, []);

  useEffect(() => {
    const handleTaskStatus = (snapshot) => setTaskStatus(snapshot || { tasks: [] });
    mempoolWebSocketService.on('task_status', handleTaskStatus);
    return () => mempoolWebSocketService.off('task_status', handleTaskStatus);
  }, []);

  const persistentTasks = Array.isArray(taskStatus?.tasks) ? taskStatus.tasks : [];
  const persistentEvents = Array.isArray(taskStatus?.recent_events) ? taskStatus.recent_events : [];
  const stopTask = async (task) => {
    if (!task?.id || stoppingTaskId) return;
    setStoppingTaskId(task.id);
    try {
      const nextStatus = await backendRpcService.stopPersistentTask(task.id);
      setTaskStatus(nextStatus || { tasks: [] });
      if (!nextStatus?.tasks?.length) setTaskMenuAnchor(null);
    } finally {
      setStoppingTaskId('');
    }
  };

  const chainConfig = SUPPORTED_CHAINS[selectedChain];
  const currentBackendStatus = backendStatus || { state: 'disconnected', lastError: '' };
  const updateLabel = rpcStatus.connected
    ? rpcStatus.connectionType === 'ws'
      ? 'WebSocket'
      : formatIntervalSeconds(rpcStatus.updateInterval || chainConfig?.blockTime)
    : '未啟動';
  const connectedEndpoints = Array.isArray(rpcStatus.endpoints)
    ? rpcStatus.endpoints.filter((endpoint) => endpoint?.connected !== false)
    : [];
  const connectedCount = Number(
    rpcStatus.connectedCount
    || connectedEndpoints.length
    || rpcStatus.urls?.length
    || (rpcStatus.connected ? 1 : 0)
  );
  const pendingCount = connectedEndpoints.length > 0
    ? connectedEndpoints.filter((endpoint) => endpoint?.connectionType === 'ws').length
    : (rpcStatus.urls || []).filter((url) => /^wss?:\/\//i.test(url)).length;

  return (
    <Box
      sx={{
        borderBottom: '1px solid rgba(144, 202, 249, 0.12)',
        bgcolor: 'rgba(10, 25, 41, 0.78)',
        backdropFilter: 'blur(14px)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 0.75, sm: 1 } }}>
        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: 0.25, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            狀態
          </Typography>
          <Chip
            size="small"
            label={chainConfig ? `${chainConfig.name} (${chainConfig.id})` : '未選擇鏈'}
            color={chainConfig ? 'info' : 'default'}
          />
          <Chip
            size="small"
            icon={rpcStatus.connected ? <ConnectedIcon /> : <IdleIcon />}
            label={rpcStatus.connected
              ? `RPC ${connectedCount} 節點`
              : rpcStatus.connecting ? 'RPC 連接中' : 'RPC 未連接'}
            color={rpcStatus.connected ? 'success' : rpcStatus.connecting ? 'warning' : 'default'}
          />
          {rpcStatus.connected && (
            <Chip
              size="small"
              label={`Pending ${pendingCount} · 廣播 ${connectedCount}`}
              color={pendingCount > 0 ? 'success' : 'warning'}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            />
          )}
          <Chip
            size="small"
            label={`Block: ${rpcStatus.blockNumber ? rpcStatus.blockNumber.toLocaleString() : '--'}`}
            sx={{ display: { xs: rpcStatus.connected ? 'inline-flex' : 'none', sm: 'inline-flex' } }}
          />
          <Chip
            size="small"
            icon={<SyncIcon />}
            label={`Update: ${updateLabel}`}
            color={rpcStatus.connected ? 'info' : 'default'}
            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          />
          <Chip
            size="small"
            icon={currentBackendStatus.lastError ? <ErrorIcon /> : <BackendIcon />}
            label={getBackendLabel(currentBackendStatus.state)}
            color={currentBackendStatus.lastError ? 'error' : getBackendColor(currentBackendStatus.state)}
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          />
          {persistentTasks.length > 0 && (
            <Chip
              size="small"
              icon={<TaskIcon />}
              label={`常駐任務 ${persistentTasks.length}`}
              color="success"
              clickable
              onClick={(event) => setTaskMenuAnchor(event.currentTarget)}
            />
          )}
          <Chip
            size="small"
            label={`DEX: ${selectedDex?.name || '未選擇'}`}
            color={selectedDex ? 'secondary' : 'default'}
            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          />
        </Stack>
      </Container>
      <Menu
        anchorEl={taskMenuAnchor}
        open={Boolean(taskMenuAnchor)}
        onClose={() => setTaskMenuAnchor(null)}
        slotProps={{ paper: { sx: { width: 320, maxWidth: 'calc(100vw - 16px)' } } }}
      >
        {persistentTasks.map((task) => {
          const label = PERSISTENT_TASK_LABELS[task.kind] || task.kind || '後端任務';
          const eventCount = persistentEvents.filter((event) => event?.task_kind === task.kind).length;
          return (
            <MenuItem key={task.id} disableRipple sx={{ gap: 1, minWidth: 0 }}>
              <ListItemText
                primary={label}
                secondary={`${task.status === 'running' ? '執行中' : task.status} · 最近紀錄 ${eventCount}`}
                primaryTypographyProps={{ noWrap: true }}
              />
              <Tooltip title={`停止 ${label}`}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`停止 ${label}`}
                    disabled={stoppingTaskId === task.id}
                    onClick={() => stopTask(task)}
                  >
                    <StopIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default GlobalStatusBar;
